import {
  deserializeYesplanAPIDate,
  isOnSameDate,
  serializeYesplanAPIDate
} from './dateTimeUtilities';
import type {
  ActiveFilter,
  ListViewConfiguration,
  ListViewDate,
  ListViewMode,
  ListViewScreenSize
} from '../listview/types';

export const listViewDashboardType = 'listview';
export const listViewSchemaVersion = 2;
export const listViewSource = 'resourcebookings';

/* Stays 1 as fields are added: a client of an older version compares this with
   !== and discards the whole document, so bumping it would throw away a user's
   filters and view mode. A new optional field needs no bump — every field is
   validated separately (see restoredViewSettings), so a client that does not
   know one simply does not read it. */
export const viewSettingsSchemaVersion = 1;

/* 0 means auto-refresh is off. */
export const refreshIntervalOptions = [0, 15, 30, 60];

/* The stored document. Every field beyond schemaVersion is optional: an older
   document simply carries fewer of them. */
export interface StoredViewSettings {
  schemaVersion: number;
  viewModeOnSmallScreen?: string;
  viewModeOnLargeScreen?: string;
  showChildren?: boolean;
  hidePastBookingsOnToday?: boolean;
  activeFilters?: ActiveFilter[];
  /* The selected day, as YYYY-MM-DD */
  date?: string;
  /* Whether that day was the current day when it was stored */
  dateFollowsToday?: boolean;
  refreshIntervalMinutes?: number;
}

export interface ViewSettingsState {
  viewModeChoices: ViewModeChoices;
  showChildren: boolean;
  hidePastBookingsOnToday: boolean;
  activeFilters: ActiveFilter[];
  date: ListViewDate;
  refreshIntervalMinutes: number;
}

export type ViewModeChoices = Record<ListViewScreenSize, ListViewMode | null>;

export function noViewModeChoices(): ViewModeChoices {
  return { small: null, large: null };
}

function defaultViewModeOn(screenSize: ListViewScreenSize): ListViewMode {
  return screenSize === 'small' ? 'card' : 'table';
}

export function viewModeOn(
  choices: ViewModeChoices,
  screenSize: ListViewScreenSize
): ListViewMode {
  return choices[screenSize] || defaultViewModeOn(screenSize);
}

export function viewModeChoicesWith(
  choices: ViewModeChoices,
  screenSize: ListViewScreenSize,
  viewMode: ListViewMode
): ViewModeChoices {
  return { ...choices, [screenSize]: viewMode };
}

/* A stored mode this client can render: null for an absent field, or for one
   only a newer client knows */
function storedViewMode(value: string | undefined): ListViewMode | null {
  return value === 'table' || value === 'card' ? value : null;
}

export function listViewTitle(configuration: ListViewConfiguration): string {
  return configuration.name || configuration.id;
}

/* The dashboard documents among the tenant's that this client can render, in
   the order the views are offered in. A configuration holds nothing to order
   by, and the order Firestore returns them in is the order of their document
   ids - which no user ever sees - so they are ordered by what they are called */
export function listViewConfigurationsIn(
  dashboards: ListViewConfiguration[] | null
): ListViewConfiguration[] {
  if (!dashboards) return [];
  return dashboards
    .filter(
      (dashboard) =>
        dashboard.type === listViewDashboardType &&
        dashboard.schemaVersion === listViewSchemaVersion &&
        dashboard.source === listViewSource
    )
    .sort((one, other) => listViewTitle(one).localeCompare(listViewTitle(other)));
}

/* The settings of a stored document that can be trusted, as a patch to apply
   over the defaults. A document of an unknown schema version is ignored
   wholesale; within a known one every field is validated separately, so one
   unusable value (say an unparseable date) does not discard the rest. */
export function restoredViewSettings(
  stored: StoredViewSettings | null | undefined,
  today: ListViewDate
): Partial<ViewSettingsState> {
  if (!stored || stored.schemaVersion !== viewSettingsSchemaVersion) return {};
  const restored: Partial<ViewSettingsState> = {};
  const viewModeChoices: ViewModeChoices = {
    small: storedViewMode(stored.viewModeOnSmallScreen),
    large: storedViewMode(stored.viewModeOnLargeScreen)
  };
  if (viewModeChoices.small || viewModeChoices.large)
    restored.viewModeChoices = viewModeChoices;
  if (typeof stored.showChildren === 'boolean')
    restored.showChildren = stored.showChildren;
  if (typeof stored.hidePastBookingsOnToday === 'boolean')
    restored.hidePastBookingsOnToday = stored.hidePastBookingsOnToday;
  if (Array.isArray(stored.activeFilters)) restored.activeFilters = stored.activeFilters;
  if (stored.dateFollowsToday === true) restored.date = today;
  else if (stored.date) {
    try {
      restored.date = deserializeYesplanAPIDate(stored.date);
    } catch (error) {
      /* An unparseable stored date is ignored; today stays */
    }
  }
  if (
    typeof stored.refreshIntervalMinutes === 'number' &&
    refreshIntervalOptions.includes(stored.refreshIntervalMinutes)
  )
    restored.refreshIntervalMinutes = stored.refreshIntervalMinutes;
  return restored;
}

export function serializedViewSettings(
  state: ViewSettingsState,
  today: ListViewDate
): StoredViewSettings {
  const { small, large } = state.viewModeChoices;
  return {
    schemaVersion: viewSettingsSchemaVersion,
    /* A size with no choice is left out, so the document holds what the user
       chose rather than what a screen once showed them */
    ...(small ? { viewModeOnSmallScreen: small } : {}),
    ...(large ? { viewModeOnLargeScreen: large } : {}),
    showChildren: state.showChildren,
    hidePastBookingsOnToday: state.hidePastBookingsOnToday,
    activeFilters: state.activeFilters,
    date: serializeYesplanAPIDate(state.date),
    /* Compared on the day alone: the selected day carries whatever time it was
       picked at */
    dateFollowsToday: isOnSameDate(state.date, today),
    refreshIntervalMinutes: state.refreshIntervalMinutes
  };
}

/* One chip per field. Adding for a field that already has an active filter
   merges into it instead of creating a second one: for a text field (no
   predefined options) that means OR-ing the new term(s) into the existing
   values, since matchesFilterValues (listViewPresentation) already matches a
   filter's values as an OR. An options field (checkboxes) lets you pick
   several values in one visit to the panel already, so re-adding one there
   is still a no-op - signalled, as before, by returning the very same array
   so the caller can skip both the re-render and the save. */
export function filtersWithAdded(
  filters: ActiveFilter[],
  filter: ActiveFilter
): ActiveFilter[] {
  const existing = filters.find((f) => f.fieldKeyword === filter.fieldKeyword);
  if (!existing) return [...filters, filter];

  const usesOptions =
    filter.fieldType.toLowerCase() === 'dropdown' || filter.options.length > 0;
  if (usesOptions) return filters;

  const mergedValues = [
    ...existing.values,
    ...filter.values.filter((value) => !existing.values.includes(value))
  ];
  if (mergedValues.length === existing.values.length) return filters;
  return filters.map((current) =>
    current === existing ? { ...existing, values: mergedValues } : current
  );
}

export function filtersWithUpdated(
  filters: ActiveFilter[],
  filter: ActiveFilter
): ActiveFilter[] {
  return filters.map((existing) => (existing.id === filter.id ? filter : existing));
}

export function filtersWithout(
  filters: ActiveFilter[],
  filterID: string
): ActiveFilter[] {
  return filters.filter((filter) => filter.id !== filterID);
}
